/**
 * @module app.shared
 */

import { forEachSibling, removeChildren, prependTextNode, forEachAncestorAndSelf } from 'app/shared/lib/dom';
import { IEssetteConfig } from 'app/shared/config/config-base';

const printTargetClass = 'ess-print-target';
const printParentClass = `${printTargetClass}-parent`;
const printSiblingClass = `${printTargetClass}-sibling`;
const printPageStyleElement = 'print-page-style';

export interface IPrintable {
    prepareForPrinting?(): Promise<void>;
}

export interface IPrintMargins {
    left: number;
    right: number;
    top: number;
    bottom: number;
    scale?: boolean;
}

export interface IPrintConfiguration {
    scaleMargins: boolean;
}

export interface IPrintOptions {
    title?: string;
    size?: string;
    landscape?: boolean;
    margins?: number | IPrintMargins;
    config?: IPrintConfiguration;
}

export function prepareElementForPrint(element: Element): void {
    forEachAncestorAndSelf(element, ancestor => {
        // mark each ancestor
        ancestor.classList.add(printParentClass);
        forEachSibling(ancestor, sibling => {
            // mark each ancestor's sibling
            sibling.classList.add(printSiblingClass);
        });
    });
}

export function cleanupPrintElements(): void {
    for(var element of Array.from(document.getElementsByClassName(printParentClass))) {
        element.classList.remove(printParentClass);
    }
    for(var element of Array.from(document.getElementsByClassName(printSiblingClass))) {
        element.classList.remove(printSiblingClass);
    }
}

// scale=true means to divide by window.devicePixelRatio to work around a probably chrome bug
function ptToPx(points: number, scale: boolean): string {
    const factor = 96 / 72 / (scale ? Math.max(1, window.devicePixelRatio || 1) : 1);
    return `${Math.ceil(points * factor)}px`;
}

export function createPrintOptionsCss(options: IPrintOptions) {
    const scale = options?.config?.scaleMargins || false;
    const css: string[] = [];
    css.push('@page { ');
    if(options.size || options.landscape) {
        css.push('size:', options.size || '', options.landscape ? ' landscape' : '', ';');
    }
    if(options.margins) {
        css.push('margin:');
        if(typeof options.margins === 'number') {
            css.push(ptToPx(options.margins, scale));
        }
        else {
            css.push(
                ptToPx(options.margins.top, scale), ' ',
                ptToPx(options.margins.right, scale), ' ',
                ptToPx(options.margins.bottom, scale), ' ',
                ptToPx(options.margins.left, scale), ';');
        }
    }
    css.push(' }');
    return css.join('');
}

export function configurePagePrintOptions(options: IPrintOptions | null = null) {
    const styleElement = document.getElementById(printPageStyleElement);

    if(styleElement) {
        removeChildren(styleElement);
        if(options) {
            const style = createPrintOptionsCss(options);
            styleElement.textContent = style;
        }
    }
}

function handleBeforePrint(options: IPrintOptions): EventListener {
    return () => {
        configurePagePrintOptions(options);
        for(var element of Array.from(document.getElementsByClassName(printTargetClass))) {
            prepareElementForPrint(element);
        }
        if(options.title) {
            window.document.title = options.title;
        }
    }
}

function handleAfterPrint(callback: Function, title?: string): EventListener {
    return () => {
        cleanupPrintElements();
        configurePagePrintOptions(null);
        if(title) {
            // set document title to control how it shows up in header/footer and PDF file name
            document.title = title;
        }
        callback();
    }
}

export type PrintOptions = IPrintOptions | string | boolean;

export function lookupPrintOptions(config: IEssetteConfig, options: PrintOptions, title?: string): IPrintOptions {
    // all results merge in config so that system-wide printing tweaks can be applied
    const defaultProfile = config.printing.profiles['default'] || {};
    if(typeof options === 'boolean') {
        // true for default profile
        return { title, config: config.printing, ...defaultProfile };
    }
    else if(typeof options === 'string') {
        // string for named profile, falling back on default profile
        const profile = config.printing.profiles[options] || defaultProfile;
        return { title, config: config.printing, ...profile };
    }
    // otherwise specific options
    return { title, config: config.printing, ...options };
}

export function printElement(element: Element, options: IPrintOptions = {}): Promise<void> {
    return new Promise((resolve, reject) => {
        try {
            const callback = () => {
                try {
                    window.removeEventListener('afterprint', afterPrint);
                    window.removeEventListener('beforeprint', beforePrint);
                    element.classList.remove(printTargetClass);
                }
                finally {
                    resolve();
                }
            };
            const previousTitle = document.title;
            const beforePrint = handleBeforePrint(options);
            const afterPrint = handleAfterPrint(callback, previousTitle);

            element.classList.add(printTargetClass);
            window.addEventListener('beforeprint', beforePrint);
            window.addEventListener('afterprint', afterPrint);
            window.print();
        }
        catch(error) {
            reject(error);
        }
    });
}
